import { pushToDataLayer } from "@/lib/utils";
import { PaymentEventType, PaymentLogger } from "./sentryLogger";
import { useFormStore } from "@/contexts/paymentFlowContext";

// ============================
// Helper Functions
// ============================

/**
 * Hash email using SHA256
 */
export const hashEmail = async (email: string): Promise<string> => {
  if (!email) {
    return "";
  }
  const CryptoJS = await import("crypto-js");
  return CryptoJS.SHA256(email.toLowerCase().trim()).toString(CryptoJS.enc.Hex);
};

/**
 * Hash phone number using SHA256
 */
export const hashPhone = async (phone: string): Promise<string> => {
  if (!phone) {
    return "";
  }
  const CryptoJS = await import("crypto-js");
  // Remove any non-digit characters before hashing
  const cleanedPhone = phone.replace(/\D/g, "");
  return CryptoJS.SHA256(cleanedPhone).toString(CryptoJS.enc.Hex);
};

/**
 * Get or create session ID from sessionStorage
 */
export const getOrCreateSessionId = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const storageKey = "gtm_session_id";
  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
};

/**
 * Get user tracking data (user_id, hashed_mail, hashed_phone)
 * This function should be called from components that have access to user data
 */
export const getUserTrackingData = async (user?: {
  id?: string;
  email?: string;
  phonenumber?: string;
}): Promise<{
  user_id?: string;
  hashed_mail?: string;
  hashed_phone?: string;
}> => {
  const result: {
    user_id?: string;
    hashed_mail?: string;
    hashed_phone?: string;
  } = {};

  if (user?.id) {
    result.user_id = user.id;
  }

  if (user?.email) {
    result.hashed_mail = await hashEmail(user.email);
  }

  if (user?.phonenumber) {
    result.hashed_phone = await hashPhone(user.phonenumber);
  }

  return result;
};

/**
 * Get user tracking data from form store (for non-authenticated users)
 */
export const getUserTrackingDataFromForm = async (formData?: {
  email?: string;
  phoneNumber?: string;
}): Promise<{
  user_id?: string;
  hashed_mail?: string;
  hashed_phone?: string;
}> => {
  const formStore = formData || useFormStore.getState();
  const result: {
    user_id?: string;
    hashed_mail?: string;
    hashed_phone?: string;
  } = {};

  if (formStore.email) {
    result.hashed_mail = await hashEmail(formStore.email);
  }

  if (formStore.phoneNumber) {
    result.hashed_phone = await hashPhone(formStore.phoneNumber);
  }

  return result;
};

// ============================
// Event Tracking Functions
// ============================

/**
 * Track page view event
 */
export const trackPageView = async (pageName?: string): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  const sessionId = getOrCreateSessionId();
  const referrer = document.referrer || "";
  const pagePath = window.location.pathname;
  const pageTitle = pageName || document.title;

  pushToDataLayer({
    event: "page_view",
    session_id: sessionId,
    referrer,
    page_path: pagePath,
    page_name: pageTitle,
  });
};

/**
 * Track challenge creation event
 */
export const trackChallengeCreation = async (user?: {
  id?: string;
  email?: string;
  phonenumber?: string;
}): Promise<void> => {
  const userData = await getUserTrackingData(user);

  pushToDataLayer({
    event: "Challenge Creation",
    ...userData,
  });
};

/**
 * Track Sadaqah creation event
 */
export const trackSadaqahCreation = async (user?: {
  id?: string;
  email?: string;
  phonenumber?: string;
}): Promise<void> => {
  const userData = await getUserTrackingData(user);

  pushToDataLayer({
    event: "Sadaqah Creation",
    ...userData,
  });
};

/**
 * Track view item list event (programs list page)
 */
export const trackViewItemList = async (user?: {
  id?: string;
  email?: string;
  phonenumber?: string;
}): Promise<void> => {
  const userData = await getUserTrackingData(user);

  pushToDataLayer({
    event: "view_item_list",
    ...userData,
  });
};

/**
 * Track view item event (individual program page)
 */
export const trackViewItem = async (
  programId: string,
  programName: string,
  user?: {
    id?: string;
    email?: string;
    phonenumber?: string;
  }): Promise<void> => {
  const userData = await getUserTrackingData(user);

  pushToDataLayer({
    event: "view_item",
    program_id: programId,
    program_name: programName,
    ...userData,
  });
};

/**
 * Track add to cart event (Donate Now button click)
 */
export const trackAddToCart = async (
  programId: string,
  programName: string,
  user?: {
    id?: string;
    email?: string;
    phonenumber?: string;
  }): Promise<void> => {
  const userData = await getUserTrackingData(user);

  pushToDataLayer({
    event: "add_to_cart",
    program_id: programId,
    program_name: programName,
    ...userData,
  });
};

export interface TrackBeginCheckoutParams {
  programId: string;
  programName: string;
  value: number;
  currency: string;
  donationType?: string;
  donationPeriod?: string;
  someone?: string;
  user?: {
    id?: string;
    email?: string;
    phonenumber?: string;
  };
  formData?: {
    email?: string;
    phoneNumber?: string;
  };
}

/**
 * Track begin checkout event
 */
export const trackBeginCheckout = async (params: TrackBeginCheckoutParams): Promise<void> => {
  const {
    programId,
    programName,
    value,
    currency,
    donationType,
    donationPeriod,
    someone,
    user,
    formData,
  } = params;

  // Get user tracking data - try authenticated user first, then form data
  let userData = await getUserTrackingData(user);

  // If no user data from authenticated user, try to get from form data
  if (!userData.hashed_mail && !userData.hashed_phone) {
    const formDataTracking = await getUserTrackingDataFromForm(formData);
    userData = { ...userData, ...formDataTracking };
  }

  pushToDataLayer({
    event: "begin_checkout",
    program_id: programId,
    program_name: programName,
    value,
    currency,
    donation_type: donationType,
    dontation_period: donationPeriod, // Note: keeping typo from Excel
    someone,
    ...userData,
    items: [
      {
        item_id: programId,
        item_name: programName,
        item_category: "program",
        price: value,
        quantity: 1,
      },
    ],
  });
};

/**
 * Track add payment info event
 */
export const trackAddPaymentInfo = async (
  paymentMethod: string,
  user?: {
    id?: string;
    email?: string;
    phonenumber?: string;
  }): Promise<void> => {
  const userData = await getUserTrackingData(user);

  pushToDataLayer({
    event: "add_payment_info",
    payment_method: paymentMethod,
    ...userData,
  });
};

// ============================
// Purchase Event (Updated)
// ============================

export interface TrackPurchaseParams {
  donationId: string;
  amount: string; // Format: "EGP 100" or "USD 50"
  itemId: string; // Program ID or Challenge ID
  itemName: string; // Program title or Challenge name
  itemType?: "program" | "challenge"; // Type of item being purchased
  paymentMethod?: "card" | "cash"; // Payment method used
  transactionId?: string; // Transaction ID
  donationType?: string; // Donation type
  donationPeriod?: string; // Donation period
  city?: string; // City
  user?: {
    id?: string;
    email?: string;
    phonenumber?: string;
  }; // User data
}

export const trackPurchaseEvent = async (params: TrackPurchaseParams): Promise<void> => {
  const {
    donationId,
    amount,
    itemId,
    itemName,
    itemType = "program",
    paymentMethod,
    transactionId,
    donationType,
    donationPeriod,
    city,
    user,
  } = params;

  const parts = amount.trim().split(/\s+/);
  const originalCurrency = parts[0] || "EGP";
  const numericAmount = parseFloat(parts[1] || "0");

  if (!numericAmount || isNaN(numericAmount)) {
    PaymentLogger.logPaymentEvent(
      PaymentEventType.PAYMENT_FAILED,
      "[Analytics] Invalid amount",
      { donationId, amount },
      "warning",
    );
    return;
  }

  let currency = originalCurrency;
  let finalAmount = numericAmount;

  if (currency === "USD") {
    finalAmount = numericAmount * 48;
    currency = "EGP";
  }

  // Get user tracking data
  const userData = await getUserTrackingData(user);

  // ============================
  // ✅ GTM PURCHASE EVENT
  // ============================
  pushToDataLayer({
    event: "purchase",
    transaction_id: transactionId || donationId,
    donation_id: donationId,
    program_id: itemId,
    program_name: itemName,
    value: finalAmount,
    currency,
    payment_method: paymentMethod,
    donation_type: donationType,
    dontation_period: donationPeriod, // Note: keeping typo from Excel
    city,
    ...userData,
    items: [
      {
        item_id: itemId,
        item_name: itemName,
        item_category: itemType,
        price: finalAmount,
        quantity: 1,
      },
    ],
  });

  PaymentLogger.logPaymentEvent(
    PaymentEventType.PAYMENT_SUCCESS,
    "Analytics: Purchase pushed to GTM",
    {
      donationId,
      amount: finalAmount,
      currency,
      programId: itemId,
    },
    "info",
  );
};
